import type {
  Formateur as StorageFormateur,
  Formation as StorageFormation,
  FormationDomain,
  FormationLevel,
  ProgrammeModule as StorageProgrammeModule,
  Session,
} from '@mtc/shared';
import type {
  Formateur,
  FormateurCouleur,
  Formation,
  FormationCouleur,
  FormationDomaine,
  FormationNiveau,
  FormationSession,
  ModuleDate,
  ProgrammeModule,
} from '../types';

// ──────────────────────────────────────────────────────────────────────────
// Translation tables: canonical (English/slug) → display (French)
// ──────────────────────────────────────────────────────────────────────────
const DOMAIN_LABEL: Record<FormationDomain, FormationDomaine> = {
  implantologie: 'Implantologie',
  orthodontie: 'Orthodontie',
  endodontie: 'Endodontie',
  parodontologie: 'Parodontologie',
  'chirurgie-orale': 'Chirurgie orale',
  pedodontie: 'Pédodontie',
  esthetique: 'Esthétique dentaire',
};

const LEVEL_LABEL: Record<FormationLevel, FormationNiveau> = {
  initiation: 'Débutant',
  intermediaire: 'Intermédiaire',
  avance: 'Avancé',
  'tous-niveaux': 'Tous niveaux',
};

const COLOR_THEME: Record<NonNullable<StorageFormateur['colorTheme']>, FormateurCouleur> = {
  vert: 'vert',
  rouge: 'rouge',
  or: 'or',
};

const MONTHS_FR = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
] as const;

/**
 * Parse the YYYY-MM-DD portion of an ISO string as a LOCAL-time date.
 *
 * Background: admin date inputs save values like `"2026-07-18T23:30:00Z"`.
 * `new Date(iso)` interprets that in UTC, but `.getDate()/.getMonth()` read
 * in local time, so for Morocco (UTC+1) the day rolls over to the 19th.
 * That made same-month module ranges render as cross-month strings
 * ("17 juillet — 18 juillet 2026" instead of "17 — 18 juillet 2026"),
 * which then surfaced as "17 juillet-18 juillet" after compaction.
 *
 * By taking just the date portion and constructing a local Date, we treat
 * every ISO as "the calendar day the admin typed" regardless of TZ.
 */
function dayOnly(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m) {
    return new Date(
      parseInt(m[1]!, 10),
      parseInt(m[2]!, 10) - 1,
      parseInt(m[3]!, 10),
    );
  }
  return new Date(iso);
}

/** "3 jours", "2 jours", "1 jour" — derived from durationHours assuming 8h/day. */
function formatDuration(hours: number): string {
  const days = Math.max(1, Math.round(hours / 8));
  return days === 1 ? '1 jour' : `${days} jours`;
}

/** Format a date range, French style ("12 — 14 juin 2026", "23 mai 2026"). */
function formatRange(startIso: string, endIso: string): string {
  const start = dayOnly(startIso);
  const end = dayOnly(endIso);
  if (Number.isNaN(start.getTime())) return '';
  if (Number.isNaN(end.getTime())) return formatShort(startIso);
  const startMonth = MONTHS_FR[start.getMonth()];
  const endMonth = MONTHS_FR[end.getMonth()];
  const sameMonth =
    start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const sameDay = sameMonth && start.getDate() === end.getDate();
  if (sameDay) return `${start.getDate()} ${startMonth} ${start.getFullYear()}`;
  if (sameMonth) return `${start.getDate()} — ${end.getDate()} ${startMonth} ${end.getFullYear()}`;
  return `${start.getDate()} ${startMonth} — ${end.getDate()} ${endMonth} ${end.getFullYear()}`;
}

function formatShort(iso: string): string {
  const d = dayOnly(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
}

function adaptProgramme(programme: StorageProgrammeModule[]): ProgrammeModule[] {
  return programme
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((m) => ({
      module: `Module ${m.order}`,
      titre: m.title,
      points: m.bullets,
      formateurIds: m.formateurIds ?? [],
    }));
}

/** Derive the session's earliest start ISO from its moduleDates (or legacy startDate). */
function firstStartIso(s: Session): string {
  const md = s.moduleDates ?? [];
  if (md.length > 0) return md[0]!.startDate;
  return s.startDate ?? '';
}

function lastEndIso(s: Session): string {
  const md = s.moduleDates ?? [];
  if (md.length > 0) return md[md.length - 1]!.endDate;
  return s.endDate ?? s.startDate ?? '';
}

/** A session is "upcoming" if its first-module start date is today or in the future. */
function isUpcoming(s: Session, now: number = Date.now()): boolean {
  const iso = firstStartIso(s);
  const t = new Date(iso).getTime();
  // 24h grace so a session starting today doesn't drop off mid-morning.
  return !Number.isNaN(t) && t >= now - 24 * 3600 * 1000;
}

function adaptModuleDates(s: Session): ModuleDate[] {
  const md = s.moduleDates ?? [];
  return md.map((d) => ({
    range: formatRange(d.startDate, d.endDate),
    short: formatShort(d.startDate),
    startDate: d.startDate,
  }));
}

/**
 * Tighten a range string for compact card display.
 *
 *  - "20 — 21 mars 2026"        → "20-21 mars 2026"
 *  - "10 juin — 11 juin 2026"   → "10-11 juin 2026"  (collapsed)
 *  - "10 juin — 11 juin"        → "10-11 juin"       (collapsed, no year)
 *  - "30 octobre — 1 novembre"  → "30 octobre-1 novembre" (genuine cross-month)
 *
 * The collapse step is what fixes the "10 juin-11 juin" leak: even when the
 * underlying data takes the cross-month branch of `formatRange` (typo years,
 * mixed ISO formats, etc.), we still produce the compact form because both
 * halves share the same month token.
 */
function tightenRange(range: string): string {
  // 1) replace em-dash-with-spaces by a tight hyphen
  const dashTight = range.replace(/ — /g, '-');
  // 2) if both halves carry the SAME month name, drop the duplicate
  //    Pattern: "DD MONTH-DD MONTH [YEAR]" → "DD-DD MONTH [YEAR]"
  //    \p{L}+ matches Unicode letters (handles "février", "août", …).
  return dashTight.replace(
    /^(\d+)\s+(\p{L}+)-(\d+)\s+\2(\s+\d{4})?$/u,
    (_full, d1: string, mon: string, d2: string, year?: string) =>
      `${d1}-${d2} ${mon}${year ?? ''}`,
  );
}

/**
 * Compact, single-line per-module date summary used in card meta rows.
 * Strips the year on all but the trailing range so
 * "20-21 mars 2026" + "5-6 avril 2026" → "20-21 mars · 5-6 avril 2026".
 */
function formatModuleDatesShort(moduleDates: ModuleDate[]): string {
  if (moduleDates.length === 0) return '';
  if (moduleDates.length === 1) return tightenRange(moduleDates[0]!.range);
  return moduleDates
    .map((md, i) => {
      const isLast = i === moduleDates.length - 1;
      const tight = tightenRange(md.range);
      return isLast ? tight : tight.replace(/\s+\d{4}$/, '');
    })
    .join(' · ');
}

function adaptSession(s: Session): FormationSession {
  const seats = s.seats ?? 0;
  const booked = s.seatsBooked ?? 0;
  const remaining = Math.max(0, seats - booked);
  const start = firstStartIso(s);
  const end = lastEndIso(s);
  // Map legacy registrationOpen → registrationStatus when the new field isn't set.
  const status: 'open' | 'soldout' | 'custom' =
    s.registrationStatus ?? (s.registrationOpen === false ? 'soldout' : 'open');
  const moduleDates = adaptModuleDates(s);
  const overallRange = formatRange(start, end);
  return {
    id: s.id,
    nom: s.name ?? 'Édition',
    startDate: start,
    dates: overallRange,
    dateShort: formatShort(start),
    ville: s.city,
    lieu: s.venue ?? s.city,
    places: seats,
    placesRestantes: remaining,
    moduleDates,
    moduleDatesShort: formatModuleDatesShort(moduleDates) || overallRange,
    registrationStatus: status,
    customStatus: s.customStatus ?? '',
    registrationOpen: status === 'open' && remaining > 0,
  };
}

// ──────────────────────────────────────────────────────────────────────────
// Formation
// ──────────────────────────────────────────────────────────────────────────
export function adaptFormation(f: StorageFormation): Formation {
  // Filter out archived (legacy/past) and non-upcoming sessions, then sort the
  // rest by first-module start ascending. Archived éditions are admin-only —
  // visitors never see them and can't register for them.
  const upcoming = f.sessions
    .filter((s) => !s.archived && isUpcoming(s))
    .slice()
    .sort((a, b) => firstStartIso(a).localeCompare(firstStartIso(b)));

  const upcomingSessions = upcoming.map(adaptSession);
  const first: FormationSession | undefined = upcomingSessions[0];

  // Derive a single status label/tone for cards (mirrors `first` like `dates` does).
  let statusLabel: string;
  let statusTone: 'open' | 'soldout' | 'custom' | 'none';
  if (!first) {
    statusLabel = 'Réserver votre place';
    statusTone = 'none';
  } else if (first.registrationStatus === 'soldout' || first.placesRestantes === 0) {
    statusLabel = 'Complète';
    statusTone = 'soldout';
  } else if (first.registrationStatus === 'custom') {
    statusLabel = first.customStatus || 'Sur demande';
    statusTone = 'custom';
  } else {
    statusLabel = 'Inscriptions ouvertes';
    statusTone = 'open';
  }

  return {
    id: f.slug,
    sessionId: first?.id ?? '',
    titre: f.title,
    domaine: DOMAIN_LABEL[f.domain],
    niveau: LEVEL_LABEL[f.level],
    duree: formatDuration(f.durationHours),
    heures: f.durationHours,
    dates: first?.dates ?? 'Dates à confirmer',
    moduleDatesShort: first?.moduleDatesShort ?? 'Dates à confirmer',
    dateStart: first?.startDate.split('T')[0] ?? '',
    ville: first?.ville ?? '',
    lieu: first?.lieu ?? '',
    places: first?.places ?? 0,
    placesRestantes: first?.placesRestantes ?? 0,
    statusLabel,
    statusTone,
    upcomingSessions,
    prix: f.price,
    // Fall back to (price / numModules) when admin hasn't set prixParModule yet.
    prixParModule:
      f.prixParModule && f.prixParModule > 0
        ? f.prixParModule
        : Math.round(f.price / Math.max(1, f.programme.length)),
    devise: f.currency,
    intervenants: f.formateurIds,
    accroche: f.shortDescription,
    description: f.description,
    programme: adaptProgramme(f.programme),
    tags: f.tags,
    image: f.domain, // placeholder label when no heroImage is set
    heroImage: f.heroImage,
    programmePdf: f.programmePdf,
    featured: f.featured,
    couleur: (f.colorTheme ?? 'vert') as FormationCouleur,
    practicalInfo: {
      lieu: f.practicalInfo?.lieu?.trim() ?? '',
      inclus: f.practicalInfo?.inclus?.trim() ?? '',
      prerequis: f.practicalInfo?.prerequis?.trim() ?? '',
      annulation: f.practicalInfo?.annulation?.trim() ?? '',
    },
  };
}

// ──────────────────────────────────────────────────────────────────────────
// Formateur
// ──────────────────────────────────────────────────────────────────────────
export function adaptFormateur(fo: StorageFormateur): Formateur {
  return {
    id: fo.slug,
    nom: fo.name,
    titre: fo.title,
    bio: fo.bio,
    ville: fo.city,
    formations: fo.formationIds,
    couleur: COLOR_THEME[fo.colorTheme],
    photo: fo.photo,
    linkedinUrl: fo.linkedinUrl?.trim() ? fo.linkedinUrl.trim() : null,
    instagramUrl: fo.instagramUrl?.trim() ? fo.instagramUrl.trim() : null,
    facebookUrl: fo.facebookUrl?.trim() ? fo.facebookUrl.trim() : null,
    tiktokUrl: fo.tiktokUrl?.trim() ? fo.tiktokUrl.trim() : null,
    email: fo.email?.trim() ? fo.email.trim() : null,
  };
}
